import React, { useEffect, useState, useRef } from 'react';
import { CreditCardIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, CheckIcon, BarChart2Icon } from 'lucide-react';
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-9882">
      <button type="button" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D]" data-id="element-9883">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-9884" />}
        <span className="truncate" data-id="element-9885">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-9886" />
      </button>
      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-9887">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#F7941D]/5 font-medium' : 'text-gray-700'}`} data-id="element-9888">
              <div className="flex items-center gap-2" data-id="element-9889">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-9890" />}
                <span data-id="element-9891">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#F7941D] flex-shrink-0" data-id="element-9892" />}
            </button>)}
        </div>}
    </div>;
}
interface Terminal {
  id: string;
  name: string;
  serialNumber: string;
  model: string;
  brand: string;
  status: 'online' | 'offline' | 'maintenance';
  location: string;
  lastConnection: string;
  transactionVolume: number;
  batteryLevel?: number;
  firmwareVersion: string;
}
const terminals: Terminal[] = [{
  id: 'term-001',
  name: 'Front Office 1',
  serialNumber: 'VFN-V240m-12345',
  model: 'Verifone V240m',
  brand: 'Verifone',
  status: 'online',
  location: 'Main Reception',
  lastConnection: '2 minutes ago',
  transactionVolume: 12450,
  batteryLevel: 85,
  firmwareVersion: '4.3.1'
}, {
  id: 'term-002',
  name: 'Front Office 2',
  serialNumber: 'VFN-V400m-98765',
  model: 'Verifone V400m',
  brand: 'Verifone',
  status: 'online',
  location: 'Main Reception',
  lastConnection: '5 minutes ago',
  transactionVolume: 9870,
  batteryLevel: 72,
  firmwareVersion: '2.1.0'
}, {
  id: 'term-003',
  name: 'Oblu Restaurant',
  serialNumber: 'VFN-T650p-34567',
  model: 'Verifone T650p',
  brand: 'Verifone',
  status: 'offline',
  location: 'Main Restaurant',
  lastConnection: '3 days ago',
  transactionVolume: 5430,
  batteryLevel: 0,
  firmwareVersion: '3.0.2'
}, {
  id: 'term-004',
  name: 'Ocean Bar',
  serialNumber: 'VFN-M400-56789',
  model: 'Verifone M400',
  brand: 'Verifone',
  status: 'online',
  location: 'Beachfront',
  lastConnection: '17 minutes ago',
  transactionVolume: 3250,
  batteryLevel: 45,
  firmwareVersion: '4.2.1'
}, {
  id: 'term-005',
  name: 'Spa Reception',
  serialNumber: 'VFN-V200c-54321',
  model: 'Verifone V200c',
  brand: 'Verifone',
  status: 'maintenance',
  location: 'Wellness Center',
  lastConnection: '1 day ago',
  transactionVolume: 7650,
  batteryLevel: 100,
  firmwareVersion: '1.9.5'
}, {
  id: 'term-006',
  name: 'Water Sports Kiosk',
  serialNumber: 'VFN-e280-67890',
  model: 'Verifone e280',
  brand: 'Verifone',
  status: 'online',
  location: 'Beach Activity Center',
  lastConnection: '1 hour ago',
  transactionVolume: 2180,
  firmwareVersion: '2.5.0'
}, {
  id: 'term-007',
  name: 'Sunset Lounge',
  serialNumber: 'VFN-P400-24680',
  model: 'Verifone P400',
  brand: 'Verifone',
  status: 'online',
  location: 'Rooftop Bar',
  lastConnection: '10 minutes ago',
  transactionVolume: 8920,
  firmwareVersion: '3.1.2'
}, {
  id: 'term-008',
  name: 'Gift Shop',
  serialNumber: 'VFN-e355-13579',
  model: 'Verifone e355',
  brand: 'Verifone',
  status: 'offline',
  location: 'Lobby Area',
  lastConnection: '2 days ago',
  transactionVolume: 1540,
  batteryLevel: 0,
  firmwareVersion: '2.0.3'
}];
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
const getStatusBadge = (status: 'online' | 'offline' | 'maintenance') => {
  const styles = {
    online: 'bg-green-50 text-green-700',
    offline: 'bg-red-50 text-red-700',
    maintenance: 'bg-amber-50 text-amber-700'
  };
  const icons = {
    online: <CheckCircleIcon size={11} className="mr-1" data-id="element-9893" />,
    offline: <XCircleIcon size={11} className="mr-1" data-id="element-9894" />,
    maintenance: <AlertTriangleIcon size={11} className="mr-1" data-id="element-9895" />
  };
  const labels = {
    online: 'Online',
    offline: 'Offline',
    maintenance: 'Maintenance'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-9896">
      {icons[status]}
      {labels[status]}
    </span>;
};
const getBatteryBar = (level?: number) => {
  if (level === undefined) return <span className="text-xs text-gray-400" data-id="element-9897">—</span>;
  const color = level < 20 ? 'bg-red-500' : level < 50 ? 'bg-amber-500' : 'bg-green-500';
  return <div className="flex items-center gap-1.5" data-id="element-9898">
      <div className="w-7 h-2.5 border border-gray-300 rounded-[3px] overflow-hidden relative" data-id="element-9899">
        <div className={`absolute inset-y-0 left-0 ${color} rounded-[2px]`} style={{
        width: `${level}%`
      }} data-id="element-9900" />
      </div>
      <span className="text-xs text-gray-500" data-id="element-9901">{level}%</span>
    </div>;
};
export function PhysicalTerminals() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const filteredTerminals = terminals.filter(t => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = t.name.toLowerCase().includes(q) || t.serialNumber.toLowerCase().includes(q) || t.model.toLowerCase().includes(q) || t.location.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'all' || t.status === statusFilter);
  });
  const totalDevices = terminals.length;
  const onlineDevices = terminals.filter(t => t.status === 'online').length;
  const totalVolume = terminals.reduce((s, t) => s + t.transactionVolume, 0);
  return <div className="space-y-5" data-id="element-9902">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-9903">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-9904">
          <div className="h-10 w-10 rounded-lg bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0" data-id="element-9905">
            <CreditCardIcon size={18} className="text-[#F7941D]" data-id="element-9906" />
          </div>
          <div data-id="element-9907">
            <p className="text-xs text-gray-500" data-id="element-9908">Total terminals</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-9909">{totalDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-9910">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-9911">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-9912" />
          </div>
          <div data-id="element-9913">
            <p className="text-xs text-gray-500" data-id="element-9914">Online terminals</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-9915">{onlineDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-9916">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-9917">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-9918" />
          </div>
          <div data-id="element-9919">
            <p className="text-xs text-gray-500" data-id="element-9920">Total volume</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-9921">
              THB {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-9922">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-9923">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-9924">
            <CreditCardIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-9925" />
            Physical terminals
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-9926">
          <div className="relative flex-1" data-id="element-9927">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-9928" />
            <input type="text" className={`${inputClass} pl-9`} placeholder="Search by name, serial, model, or location..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-9929" />
          </div>
          <div className="flex items-center gap-2" data-id="element-9930">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'online',
            label: 'Online',
            dot: 'bg-green-500'
          }, {
            value: 'offline',
            label: 'Offline',
            dot: 'bg-red-500'
          }, {
            value: 'maintenance',
            label: 'Maintenance',
            dot: 'bg-amber-500'
          }]} data-id="element-9931" />
            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-9932">
              <RefreshCwIcon size={13} data-id="element-9933" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ─── TERMINAL GRID ─── */}
      {filteredTerminals.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-9934">
          {filteredTerminals.map(terminal => <div key={terminal.id} className="bg-white rounded-xl border border-gray-200" data-id="element-9935">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-9936">
                <div className="flex items-center gap-2.5 min-w-0" data-id="element-9937">
                  <div className="h-8 w-8 rounded-lg bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0" data-id="element-9938">
                    <CreditCardIcon size={14} className="text-[#F7941D]" data-id="element-9939" />
                  </div>
                  <div className="min-w-0" data-id="element-9940">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-9941">
                      {terminal.name}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-9942">
                      {terminal.model}
                    </p>
                  </div>
                </div>
                {getStatusBadge(terminal.status)}
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-9943">
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-9944">
                  <span className="text-xs text-gray-500" data-id="element-9945">Serial</span>
                  <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-9946">
                    {terminal.serialNumber}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-9947">
                  <span className="text-xs text-gray-500" data-id="element-9948">Location</span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-9949">
                    {terminal.location}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-9950">
                  <span className="text-xs text-gray-500" data-id="element-9951">Last connection</span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-9952">
                    {terminal.lastConnection}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-9953">
                  <span className="text-xs text-gray-500" data-id="element-9954">Volume</span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-9955">
                    THB {terminal.transactionVolume.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-9956">
                  <span className="text-xs text-gray-500" data-id="element-9957">Firmware</span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-9958">
                    v{terminal.firmwareVersion}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5" data-id="element-9959">
                  <span className="text-xs text-gray-500" data-id="element-9960">Battery</span>
                  {getBatteryBar(terminal.batteryLevel)}
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gray-200 flex justify-between" data-id="element-9961">
                <button className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-9962">
                  View details
                </button>
                <button className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-9963">
                  Update firmware
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-9964">
          <CreditCardIcon size={24} className="text-gray-400 mb-3" data-id="element-9965" />
          <p className="text-sm font-medium text-gray-700" data-id="element-9966">
            No terminals found
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-9967">
            No terminals match your search criteria. Try adjusting your filters
            or add a new terminal.
          </p>
        </div>}
    </div>;
}